/**
 * Follow Action Hooks
 * React Query mutation hooks for follow operations
 */

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import * as followService from "../services/follow.service"

/**
 * Hook for follow/unfollow mutations
 */
export function useFollowActions() {
  const queryClient = useQueryClient()

  const followMutation = useMutation({
    mutationFn: (userId: string) => followService.followUser(userId),
    onSuccess: (data, userId) => {
      queryClient.invalidateQueries({ queryKey: ["follow-status", userId] })
      // Invalidate all followers/following queries for any user (including current user's lists)
      queryClient.invalidateQueries({ queryKey: ["followers"] })
      queryClient.invalidateQueries({ queryKey: ["following"] })
      queryClient.invalidateQueries({ queryKey: ["profileStatistics", userId] })
      queryClient.invalidateQueries({ queryKey: ["profile"] })
      queryClient.invalidateQueries({ queryKey: ["community-members"] })
      queryClient.invalidateQueries({ queryKey: ["trusted-ritualists"] })
      queryClient.invalidateQueries({ queryKey: ["search"] })
      toast.success(data.message)
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || "Failed to follow user")
    },
  })

  const unfollowMutation = useMutation({
    mutationFn: (userId: string) => followService.unfollowUser(userId),
    onSuccess: (data, userId) => {
      queryClient.invalidateQueries({ queryKey: ["follow-status", userId] })
      // Invalidate all followers/following queries for any user (including current user's lists)
      queryClient.invalidateQueries({ queryKey: ["followers"] })
      queryClient.invalidateQueries({ queryKey: ["following"] })
      queryClient.invalidateQueries({ queryKey: ["profileStatistics", userId] })
      queryClient.invalidateQueries({ queryKey: ["profile"] })
      queryClient.invalidateQueries({ queryKey: ["community-members"] })
      queryClient.invalidateQueries({ queryKey: ["trusted-ritualists"] })
      queryClient.invalidateQueries({ queryKey: ["search"] })
      toast.success(data.message)
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || "Failed to unfollow user")
    },
  })

  const acceptRequestMutation = useMutation({
    mutationFn: (requestId: string) => followService.acceptFollowRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-requests"] })
      queryClient.invalidateQueries({ queryKey: ["followers"] })
      queryClient.invalidateQueries({ queryKey: ["profileStatistics"] })
      toast.success("Follow request accepted")
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || "Failed to accept request")
    },
  })

  const rejectRequestMutation = useMutation({
    mutationFn: (requestId: string) => followService.rejectFollowRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pending-requests"] })
      toast.info("Follow request declined")
    },
    onError: (error: { response?: { data?: { message?: string } } }) => {
      toast.error(error.response?.data?.message || "Failed to reject request")
    },
  })

  return {
    follow: followMutation.mutate,
    followAsync: followMutation.mutateAsync,
    unfollow: unfollowMutation.mutate,
    unfollowAsync: unfollowMutation.mutateAsync,
    acceptRequest: acceptRequestMutation.mutate,
    rejectRequest: rejectRequestMutation.mutate,
    isFollowing: followMutation.isPending,
    isUnfollowing: unfollowMutation.isPending,
    isAccepting: acceptRequestMutation.isPending,
    isRejecting: rejectRequestMutation.isPending,
  }
}
